<script setup lang="ts">
import { ref, computed, nextTick, onBeforeUnmount, watch } from 'vue';
import AlphabetIndex from '@/components/ui/AlphabetIndex.vue';
import emptyBox from '@/assets/img/emptyBox.json';
import ListGroup from '@/components/ui/ListGroup.vue';
import LottieAnimation from '@/components/ui/LottieAnimation.vue';
import SearchBar from '@/components/ui/SearchBar.vue';
import Common from '@/libs/common';
import {
  ALPHABET_INDEX_MIN_LETTERS,
  FAVORITES_INDEX_KEY,
  findScrollViewport,
} from '@/libs/helpers/alphabet-index';
import { flattenAlphabetical, groupAlphabetical } from '@/libs/helpers/alphabetical-groups';
import { isCanonicalStop } from '@/libs/helpers/stop-clusters';
import { routesByStop } from '@/libs/helpers/stop-routes';
import type { Route, Stop } from '@/@types/gtfs-ressource';
import StopItem from './StopItem.vue';
import { useAppStore } from '@/stores/app';
import { useGtfsStore } from '@/stores/gtfs';
import { useListLoadMore } from '@/use/list-load-more';

const appStore = useAppStore();
const gtfsStore = useGtfsStore();

// Entries rendered past the letter jumped to, so the section can actually
// reach the top of the viewport instead of being clamped by a short list.
const LETTER_JUMP_LOOKAHEAD = 60;
const SEARCH_DEBOUNCE_MS = 150;

const search = ref<string>('');
const debouncedSearch = ref<string>('');
const listEl = ref<HTMLElement>();
const searchEl = ref<HTMLElement>();
const scrollLoadingIncrement = ref<number>(20);

let searchTimeout: number | undefined;
watch(search, value => {
  window.clearTimeout(searchTimeout);
  if (!value) {
    debouncedSearch.value = '';
    return;
  }
  searchTimeout = window.setTimeout(() => (debouncedSearch.value = value), SEARCH_DEBOUNCE_MS);
});
onBeforeUnmount(() => window.clearTimeout(searchTimeout));

// Computed :
// Sorted and normalized once per network: a search only filters this list.
const sortedStops = computed<Array<{ stop: Stop; normalizedName: string }>>(() =>
  Object.values(gtfsStore.gtfsData.stops)
    .filter(stop => isCanonicalStop(stop.stop_id, gtfsStore.canonicalOf))
    .map(stop => ({ stop, normalizedName: Common.normalize(stop.stop_name) }))
    .sort((a, b) => (a.normalizedName < b.normalizedName ? -1 : 1)),
);

const stops = computed<Array<Stop>>(() => {
  if (!debouncedSearch.value) return sortedStops.value.map(({ stop }) => stop);
  const searchedText = Common.normalize(debouncedSearch.value);
  return sortedStops.value
    .filter(({ normalizedName }) => normalizedName.includes(searchedText))
    .map(({ stop }) => stop);
});

// One shared empty list: a fresh `[]` per render would re-render every unserved stop's row.
const NO_ROUTES: Array<Route> = [];

const stopRoutes = computed<{ [stopId: string]: Array<Route> }>(() =>
  routesByStop(gtfsStore.gtfsData.routeStops ?? {}, gtfsStore.gtfsData.routes, gtfsStore.stations),
);

const favoriteStops = computed<Array<Stop>>(() =>
  stops.value.filter(stop => appStore.favorites.stops?.[stop.stop_id]),
);

// Bookmarked stops are pulled out of the alphabetical sections, so a stop is
// only ever listed once.
const stopEntries = computed(() =>
  flattenAlphabetical(
    stops.value.filter(stop => !appStore.favorites.stops?.[stop.stop_id]),
    stop => stop.stop_name,
  ),
);

const visibleGroups = computed(() => groupAlphabetical(stopEntries.value, scrollLoadingIncrement.value));

const letterOffsets = computed(() => {
  const offsets = new Map<string, number>();
  stopEntries.value.forEach((entry, index) => {
    if (entry.type === 'separator') offsets.set(entry.value, index);
  });
  return offsets;
});

const alphabetLetters = computed(() => [...letterOffsets.value.keys()]);

const indexLetters = computed(() =>
  favoriteStops.value.length ? [FAVORITES_INDEX_KEY, ...alphabetLetters.value] : alphabetLetters.value,
);

const showLetterIndex = computed(() => alphabetLetters.value.length >= ALPHABET_INDEX_MIN_LETTERS);

// Methods :
/**
 * Jumping ahead skips the incremental loading the scroll would have done, so
 * the target section has to be rendered before it can be scrolled to.
 */
async function scrollToLetter(letter: string) {
  const entryIndex = letterOffsets.value.get(letter);

  if (entryIndex !== undefined && scrollLoadingIncrement.value < entryIndex + LETTER_JUMP_LOOKAHEAD) {
    scrollLoadingIncrement.value = entryIndex + LETTER_JUMP_LOOKAHEAD;
    await nextTick();
  }

  const header = listEl.value?.querySelector<HTMLElement>(`[data-letter="${letter}"]`);
  if (!header) return;
  header.scrollIntoView({ block: 'start' });

  // The header lands at the very top of the viewport, under the sticky search bar.
  const viewport = findScrollViewport(listEl.value);
  if (viewport) viewport.scrollTop -= stickyTopHeight();
}

/**
 * Band of the viewport the list scrolls under: the sticky search bar plus the
 * gradient it fades the rows out with, which would otherwise wash out the
 * letter header the jump lands on.
 */
function stickyTopHeight() {
  const search = searchEl.value;
  if (!search) return 0;
  const fade = parseFloat(window.getComputedStyle(search, '::after').height) || 0;
  return search.offsetHeight + fade;
}

/**
 * Load data step by step to improve Vue performance. The bound is read from
 * the current list: a latched flag set by a short search result would cap the
 * full list once the search is cleared.
 */
function loadMore() {
  if (scrollLoadingIncrement.value < stopEntries.value.length) scrollLoadingIncrement.value += 25;
}

useListLoadMore(listEl, loadMore, () => scrollLoadingIncrement.value < stopEntries.value.length);
</script>

<template>
  <div class="stops-page" :class="{ 'stops-page--indexed': showLetterIndex }">
    <!-- Sticky over the whole list: it has to come first so its anchor sits at
         the top of the scrolling viewport, not below the search bar. -->
    <AlphabetIndex v-if="showLetterIndex" :letters="indexLetters" @select="scrollToLetter" />

    <!-- Search bar -->
    <div ref="searchEl" class="stops-page__search">
      <SearchBar v-model="search" :placeholder="$t('schedules.searchStop')" />
    </div>

    <!-- Stop list -->
    <div ref="listEl" class="stops-page__list">
      <ListGroup
        v-if="favoriteStops.length"
        :data-letter="FAVORITES_INDEX_KEY"
        :label="$t('favorites.title')"
      >
        <StopItem
          v-for="stop in favoriteStops"
          :key="stop.stop_id"
          :stop="stop"
          :routes="stopRoutes[stop.stop_id] ?? NO_ROUTES"
        />
      </ListGroup>

      <ListGroup
        v-for="group in visibleGroups"
        :key="group.letter"
        :data-letter="group.letter"
        :label="group.letter"
      >
        <StopItem
          v-for="stop in group.items"
          :key="stop.stop_id"
          :stop="stop"
          :routes="stopRoutes[stop.stop_id] ?? NO_ROUTES"
        />
      </ListGroup>
      <div v-if="stops.length === 0 && debouncedSearch" class="stops-page__empty">
        {{ $t('search.noResults') }}
        <LottieAnimation :animation-data="emptyBox" class="stops-page__empty-animation" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.stops-page {
  background-color: $color-white;
  padding-bottom: $spacing-5;

  // Pinned to the top of the scrolling viewport: the list slides under it,
  // hence the full-width band and its own background.
  &__search {
    background-color: $color-white;
    padding: $spacing-4 $spacing-5 $spacing-2;
    position: sticky;
    top: 0;
    z-index: 1;

    .searchbar {
      margin-bottom: 0;
    }

    // Lets the rows fade out under the bar instead of being cut by its edge.
    &::after {
      background: linear-gradient($color-white, rgba($color-white, 0));
      content: '';
      height: $spacing-5;
      left: 0;
      pointer-events: none;
      position: absolute;
      right: 0;
      top: 100%;
    }
  }

  // Gutter left free for the letter strip, so it never sits over the bookmark
  // buttons at the end of each row.
  &--indexed &__list {
    padding-right: $spacing-5;
  }

  .list-group {
    margin-top: $spacing-4;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-4;
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    padding: $spacing-2 $spacing-5;
    text-align: center;
  }

  &__empty-animation {
    width: min(160px, 50vw);
    aspect-ratio: 1;
  }
}
</style>
